import { openEraseDialog } from './erase-control.js';
import {
  DESIGN_ANSWER_VERSION,
  designCutoutAssetId,
  designerQrMinMm,
  effectiveDpi,
  qualityFor,
  zoneScene,
  type DesignImageEffect,
  type DesignQuality,
} from '@pz/scene';
import {
  chipRow,
  el,
  field,
  iconSvg,
  note,
  textBtn,
  type DesignerCtx,
} from './panels.js';
import { canAddLayer, freshImageLayer } from './state.js';
import { run } from './commands.js';
import { fillText } from './texts.js';
import { openDialog } from './overlay.js';
import { renderCropControl } from './crop-control.js';

/**
 * The Slika panel: get a photo in, tell the shopper whether it will print,
 * and let them frame it.
 *
 * The quality verdict is the part that pays for itself. A 480 px phone
 * screenshot on a 15 cm print is 80 dpi, which looks fine on the screen the
 * shopper is holding and arrives as a blurred smear; `effectiveDpi` knows
 * that before the order does and says so beside the picture. It is advice:
 * the shop always lets the shopper order what they have, so nothing here or
 * at the till refuses a photo for its pixels.
 *
 * Framing uses the shared @pz/scene rotation-aware cover math and imageCrop
 * operation: one finger pans, the sliders zoom and straighten, and the native
 * preview and print renderer consume the same ImageCrop.
 */

function qualityWord(ctx: DesignerCtx, quality: DesignQuality): string {
  if (quality === 'high') return ctx.texts.qualityHigh;
  if (quality === 'mid') return ctx.texts.qualityMid;
  return ctx.texts.qualityLow;
}

function withoutKinds(
  effects: DesignImageEffect[] | undefined,
  kinds: ReadonlyArray<DesignImageEffect['kind']>,
): DesignImageEffect[] {
  return (effects ?? []).filter((effect) => !kinds.includes(effect.kind));
}

/**
 * The pictures this session has uploaded, and the layer a "Zameni sliku" is
 * waiting for. Both are per-designer and deliberately not part of the answer:
 * a gallery of what you just uploaded is a convenience, not a design.
 */
interface UploadCache {
  session: Array<{ assetId: string; url: string; w: number; h: number }>;
  replacing?: string;
  pairing?: { url: string; expiresAt: number; cancel(): void };
}

const uploadCaches = new WeakMap<DesignerCtx, UploadCache>();

function uploadsFor(ctx: DesignerCtx): UploadCache {
  const existing = uploadCaches.get(ctx);
  if (existing) return existing;
  const fresh: UploadCache = { session: [] };
  uploadCaches.set(ctx, fresh);
  return fresh;
}

/**
 * An upload made outside the Slike panel (the quick start, a photo template):
 * registered with the renderer and kept in "Ove sesije" like any other.
 */
export function rememberUpload(ctx: DesignerCtx, upload: { assetId: string; url: string; w: number; h: number }): void {
  ctx.renderer.registerAsset(upload.assetId, upload.url);
  const cache = uploadsFor(ctx);
  if (!cache.session.some((item) => item.assetId === upload.assetId)) cache.session.unshift(upload);
}

/** The toolbar's "Zameni sliku": the next picture picked lands on this layer. */
export function startReplace(ctx: DesignerCtx, layerId: string): void {
  uploadsFor(ctx).replacing = layerId;
}

export function replaceTarget(ctx: DesignerCtx): string | undefined {
  return uploadsFor(ctx).replacing;
}

export function endReplace(ctx: DesignerCtx): void {
  delete uploadsFor(ctx).replacing;
}

export interface ImagePanelOptions {
  /**
   * Open straight into "replace this picture" mode: the next picture the
   * shopper picks lands on this layer, keeping its box and its effects. The
   * phone's Zameni cell and the desktop toolbar's "Zameni sliku" both use it.
   */
  replaceLayerId?: string;
}

export function renderImagePanel(
  body: HTMLElement,
  ctx: DesignerCtx,
  options: ImagePanelOptions = {},
): () => void {
  const cfg = ctx.config;
  const cleanups: Array<() => void> = [];
  let alive = true;
  cleanups.push(() => { alive = false; });
  const layer = ctx.selected();
  const image = layer?.kind === 'image' ? layer : undefined;

  // ── Adding a photo ──────────────────────────────────────────────────────
  const cache = uploadsFor(ctx);
  if (options.replaceLayerId) cache.replacing = options.replaceLayerId;
  const replacingId = cache.replacing;
  let replacing = replacingId
    ? ctx.store.value().layers.find((item) => item.id === replacingId && item.kind === 'image')
    : undefined;
  if (replacingId && !replacing) endReplace(ctx);
  const allow = canAddLayer(ctx.store.state(), cfg, 'image');
  const drop = el('div', 'pzd-drop');
  drop.setAttribute('role', 'button');
  drop.tabIndex = 0;
  drop.innerHTML = iconSvg('upload', 26);
  drop.append(
    el('strong', undefined, ctx.texts.upload),
    el('span', undefined, ctx.texts.uploadHint),
  );
  const file = el('input', 'pzd-file');
  file.type = 'file';
  file.accept = 'image/*';
  file.setAttribute('aria-label', ctx.texts.upload);
  const camera = el('input', 'pzd-file');
  camera.type = 'file';
  camera.accept = 'image/*';
  camera.setAttribute('capture', 'environment');
  camera.setAttribute('aria-label', ctx.texts.uploadCamera);

  let busy = false;
  const setBusy = (on: boolean): void => {
    busy = on;
    drop.classList.toggle('is-busy', on);
    const strong = drop.querySelector('strong');
    if (strong) strong.textContent = on ? ctx.texts.uploading : ctx.texts.upload;
  };

  /**
   * One place where an uploaded picture becomes a layer — or replaces the one
   * the toolbar's "Zameni sliku" pointed at. Replacing keeps the current box
   * and filters, and resets source-specific crop and background removal.
   */
  const use = (upload: { assetId: string; url: string; w: number; h: number }): void => {
    ctx.renderer.registerAsset(upload.assetId, upload.url);
    if (!cache.session.some((item) => item.assetId === upload.assetId)) cache.session.unshift(upload);
    if (!alive || !ctx.root.isConnected) return;
    if (replacing && replacing.kind === 'image') {
      const current = ctx.store.value().layers.find(item => item.id === replacing!.id);
      if (current?.kind !== 'image' || current.assetId !== replacing.assetId) return;
      const effects = current.effects?.filter(effect => effect.kind !== 'removeBackground');
      ctx.dispatch({
        type: 'update',
        id: replacing.id,
        patch: {
          assetId: upload.assetId,
          sourcePx: { w: Math.round(upload.w), h: Math.round(upload.h) },
          crop: undefined,
          erase: undefined,
          effects: effects?.length ? effects : undefined,
        },
      });
      endReplace(ctx);
      ctx.invalidate(replacing.id);
      replacing = undefined;
      ctx.refreshPanel();
      return;
    }
    const room = canAddLayer(ctx.store.state(), cfg, 'image');
    if (!room.ok) {
      ctx.toast(room.reason === 'max-images'
        ? fillText(ctx.texts.uploadTooMany, { n: room.limit ?? cfg.image.maxCount })
        : fillText(ctx.texts.maxLayers, { n: cfg.maxLayers }), 'error');
      return;
    }
    ctx.dispatch({ type: 'add', layer: freshImageLayer(ctx.rawConfig, upload, ctx.addCount()) });
    ctx.refreshPanel();
  };

  const accept = async (picked: File | undefined): Promise<void> => {
    if (!picked || busy || !alive) return;
    if (!replacing) {
      const room = canAddLayer(ctx.store.state(), cfg, 'image');
      if (!room.ok) {
        ctx.toast(room.reason === 'max-images'
          ? fillText(ctx.texts.uploadTooMany, { n: room.limit ?? cfg.image.maxCount })
          : fillText(ctx.texts.maxLayers, { n: cfg.maxLayers }), 'error');
        return;
      }
    }
    setBusy(true);
    try {
      use(await ctx.uploads.upload(picked));
    } catch {
      if (alive) ctx.toast(ctx.texts.uploadFailed, 'error');
    } finally {
      setBusy(false);
    }
  };

  file.addEventListener('change', () => { const picked = file.files?.[0]; file.value = ''; void accept(picked); });
  camera.addEventListener('change', () => { const picked = camera.files?.[0]; camera.value = ''; void accept(picked); });
  drop.addEventListener('click', () => file.click());
  drop.addEventListener('keydown', (event) => {
    const key = (event as KeyboardEvent).key;
    if (key !== 'Enter' && key !== ' ') return;
    event.preventDefault();
    file.click();
  });
  drop.addEventListener('dragover', (event) => {
    event.preventDefault();
    drop.classList.add('is-over');
  });
  drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
  drop.addEventListener('drop', (event) => {
    event.preventDefault();
    drop.classList.remove('is-over');
    void accept(event.dataTransfer?.files?.[0]);
  });

  if (replacing) body.append(note(ctx.texts.replaceMode, 'warn'), textBtn(ctx.texts.cancel, () => { replacing = undefined; endReplace(ctx); ctx.refreshPanel(); }));
  if (!allow.ok && !replacing) {
    body.appendChild(note(allow.reason === 'max-images'
      ? fillText(ctx.texts.uploadTooMany, { n: allow.limit ?? cfg.image.maxCount })
      : fillText(ctx.texts.maxLayers, { n: cfg.maxLayers }), 'warn'));
  } else {
    body.append(drop, file, camera);
    const sources = el('div', 'pzd-source-row');
    sources.append(textBtn(ctx.texts.fromComputer, () => file.click(), { icon: 'computer' }));
    // "Upload from your phone": the host mints a pairing URL, we show it as a
    // QR code and wait. Absent on shops that have not wired the seam.
    if (ctx.uploads.pairPhone) {
      sources.append(textBtn(ctx.texts.fromPhone, () => openPhonePairing(ctx, use), { icon: 'phone' }));
    }
    if (ctx.phone()) {
      sources.append(textBtn(ctx.texts.uploadCamera, () => camera.click(), { icon: 'camera' }));
    }
    body.append(sources);
    if (cache.session.length) body.append(sessionGallery(ctx, cache, use));
  }

  if (!image) {
    body.appendChild(el('div', 'pzd-hint', ctx.texts.selectLayer));
    return () => {
      for (const stop of cleanups) stop();
    };
  }

  const id = image.id;

  // ── Quality ─────────────────────────────────────────────────────────────
  const source = image.sourcePx;
  if (source) {
    // The dpi number itself never reaches the shopper (the owner's rule,
    // 2026-09-20) — only the calm quality label. `effectiveDpi` still drives
    // `qualityFor` behind the scenes.
    const dpi = effectiveDpi(source, image.box, image.crop, image.fit);
    const quality = qualityFor(dpi, cfg);
    const badge = el('div', `pzd-quality is-${quality}`, qualityWord(ctx, quality));
    const wrap = field(ctx.texts.quality, '');
    wrap.wrap.appendChild(badge);
    // A soft print is explained, not refused: the sentence stays while the
    // photo is small and goes when a bigger one replaces it.
    const soft = note(ctx.texts.qualityBlocked, 'warn');
    soft.hidden = quality !== 'low';
    wrap.wrap.appendChild(soft);
    body.appendChild(wrap.wrap);
    cleanups.push(ctx.store.subscribe(() => {
      const now = ctx.store.value().layers.find(item => item.id === id);
      if (now?.kind !== 'image' || !now.sourcePx) return;
      const dpi = effectiveDpi(now.sourcePx, now.box, now.crop, now.fit);
      const quality = qualityFor(dpi, cfg);
      badge.className = 'pzd-quality is-' + quality;
      badge.textContent = qualityWord(ctx, quality);
      soft.hidden = quality !== 'low';
    }));
  }

  // "Fit" shows the whole photograph inside its box; "Fill" crops it to the
  // box. They are the reference editor's two image buttons, and the only two
  // words a shopper needs for what is really a crop mode.
  const fitRow = el('div', 'pzd-source-row');
  fitRow.append(
    textBtn(ctx.texts.fitToZone, () => run(ctx, { op: 'imageFit', ids: [id], fit: 'contain' }),
      { className: `pzd-command${image.fit === 'contain' ? ' is-on' : ''}` }),
    textBtn(ctx.texts.fillZone, () => run(ctx, { op: 'imageFit', ids: [id], fit: 'cover' }),
      { className: `pzd-command${image.fit === 'contain' ? '' : ' is-on'}` }),
  );
  body.append(fitRow, textBtn('Gumica za sliku', () => openEraseDialog(ctx, id)));
  // ── Framing ──────────────────────────────────────────────────────────────
  if (source && image.fit !== 'contain') cleanups.push(renderCropControl(body, ctx, id));

  // ── Remove background ───────────────────────────────────────────────────
  const cutoutOn = hasCutout(image);
  if (cfg.image.allowRemoveBackground && ctx.uploads.removeBackground) {
    const cutBox = field(ctx.texts.removeBackground);
    const button = textBtn(cutoutOn ? ctx.texts.cutoutOn : ctx.texts.removeBackground, () => {
      button.disabled = true;
      button.textContent = ctx.texts.cutoutWorking;
      void toggleRemoveBackground(ctx, image).finally(() => ctx.refreshPanel());
    }, { icon: 'magic' });
    button.classList.toggle('is-on', cutoutOn);
    cutBox.wrap.appendChild(button);
    body.appendChild(cutBox.wrap);
  }

  // Filters, tiling, opacity and the white-to-transparent switch all live in
  // the Efekti panel now — one control each, in the place the reference editor
  // keeps them, instead of a second copy here that drifted out of step.
  body.appendChild(textBtn(ctx.texts.effectsTool, () => ctx.openTool('effects'), { icon: 'magic', className: 'pzd-command' }));

  body.appendChild(chipRow([
    { id: 'flipX', label: ctx.texts.flip, icon: 'flipH', on: Boolean(image.flipX) },
    { id: 'flipY', label: ctx.texts.flip, icon: 'flipV', on: Boolean(image.flipY), ariaLabel: `${ctx.texts.flip} Y` },
    { id: 'duplicate', label: ctx.texts.duplicate, icon: 'copy' },
    { id: 'remove', label: ctx.texts.remove, icon: 'trash' },
  ], (action) => {
    if (action === 'flipX') run(ctx, { op: 'flip', ids: [id], axis: 'x' });
    if (action === 'flipY') run(ctx, { op: 'flip', ids: [id], axis: 'y' });
    if (action === 'duplicate') run(ctx, { op: 'duplicate', ids: [id] });
    if (action === 'remove') run(ctx, { op: 'delete', ids: [id] });
  }));

  return () => {
    for (const stop of cleanups) stop();
  };
}

/** True when the layer is currently showing its cut-out. */
export function hasCutout(layer: { effects?: DesignImageEffect[] }): boolean {
  return (layer.effects ?? []).some((effect) => effect.kind === 'removeBackground');
}

/**
 * "Ukloni pozadinu", once, for both shells.
 *
 * The rule the two copies of this used to disagree about: the cut-out arrives
 * as a SECOND asset registered under `designCutoutAssetId(assetId)`, and the
 * layer only carries the effect — so turning it off is free, turning it back
 * on costs nothing, and the print pipeline resolves the same pair of ids.
 */
const cutoutRequests = new WeakMap<DesignerCtx, Set<string>>();
export async function toggleRemoveBackground(
  ctx: DesignerCtx,
  layer: { id: string; assetId: string; effects?: DesignImageEffect[] },
): Promise<void> {
  const current = () => {
    const now = ctx.store.value().layers.find(item => item.id === layer.id);
    return now?.kind === 'image' && now.assetId === layer.assetId && ctx.root.isConnected ? now : undefined;
  };
  const patch = (on: boolean) => {
    const now = current(); if (!now) return;
    const effects = withoutKinds(now.effects, ['removeBackground']);
    if (on) effects.push({ kind: 'removeBackground' });
    ctx.dispatch({ type: 'update', id: layer.id, patch: { effects: effects.length ? effects : undefined } });
    ctx.invalidate(layer.id);
  };
  const now = current(); if (!now) return;
  if (hasCutout(now)) { patch(false); return; }
  const pending = cutoutRequests.get(ctx) ?? new Set<string>(); cutoutRequests.set(ctx, pending);
  if (pending.has(layer.id)) return;
  if (ctx.renderer.assetUrl(designCutoutAssetId(layer.assetId))) { patch(true); return; }
  pending.add(layer.id);
  try {
    const result = await ctx.uploads.removeBackground?.(layer.assetId);
    if (!result) throw new Error('No prepared asset');
    ctx.renderer.registerAsset(designCutoutAssetId(layer.assetId), result.url);
    patch(true);
  } catch {
    if (current()) ctx.toast(ctx.texts.cutoutFailed, 'error');
  } finally { pending.delete(layer.id); }
}

/** The pictures uploaded during this session, ready to drop in again. */
function sessionGallery(
  ctx: DesignerCtx,
  cache: UploadCache,
  use: (upload: { assetId: string; url: string; w: number; h: number }) => void,
): HTMLElement {
  const box = field(ctx.texts.sessionUploads);
  const grid = el('div', 'pzd-grid');
  for (const upload of cache.session) {
    const tile = el('button', 'pzd-tile');
    tile.type = 'button';
    tile.setAttribute('aria-label', ctx.texts.replacePicked);
    const picture = el('img');
    picture.src = upload.url;
    picture.alt = '';
    picture.loading = 'lazy';
    tile.append(picture);
    tile.addEventListener('click', () => use(upload));
    grid.append(tile);
  }
  box.wrap.append(grid);
  return box.wrap;
}

/**
 * "Sa telefona" — the reference's QR pairing.
 *
 * The host owns the session (it knows the API); the designer only shows the
 * code and waits. `wait()` resolves with whatever the phone sent, or an empty
 * list when the ten minutes ran out, and the dialog says which.
 */
function openPhonePairing(
  ctx: DesignerCtx,
  use: (upload: { assetId: string; url: string; w: number; h: number }) => void,
): void {
  const pair = ctx.uploads.pairPhone;
  if (!pair) return;
  openDialog({
    ctx,
    title: ctx.texts.phoneQrTitle,
    className: 'is-pair',
    build: (body, handle) => {
      let cancelled = false;
      let session: { cancel(): void } | undefined;
      const status = el('div', 'pzd-hint', ctx.texts.loading);
      const code = el('div', 'pzd-qr-box');
      body.append(code, note(ctx.texts.phoneQrHint), status);
      void pair().then((created) => {
        if (cancelled) {
          created.cancel();
          return;
        }
        session = created;
        void paintPairingCode(ctx, code, created.url);
        const link = el('a', 'pzd-hint');
        link.href = created.url;
        link.textContent = created.url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        body.insertBefore(link, status);
        status.textContent = ctx.texts.phoneWaiting;
        return created.wait().then((assets) => {
          if (cancelled) return;
          if (!assets.length) {
            status.textContent = ctx.texts.phoneExpired;
            return;
          }
          for (const asset of assets) use(asset);
          status.textContent = fillText(ctx.texts.phoneReceived, { n: assets.length });
          handle.close();
        });
      }).catch(() => {
        if (!cancelled) status.textContent = ctx.texts.phoneFailed;
      });
      return () => {
        cancelled = true;
        session?.cancel();
      };
    },
  });
}

/**
 * The pairing URL as a real QR code.
 *
 * There is exactly one QR encoder in this product — the renderer's, the one
 * that produces the codes shoppers put on printed cards — so the dialog asks
 * it for a square of its own rather than carrying a second implementation
 * that could encode the same URL differently. When the render bundle has not
 * loaded the link below the box is still tappable, which is the whole point of
 * the dialog anyway.
 */
async function paintPairingCode(ctx: DesignerCtx, host: HTMLElement, url: string): Promise<void> {
  const raw = ctx.renderer.renderRaw;
  if (!raw) return;
  const side = Math.max(designerQrMinMm(url), 30);
  const config = { sizeMm: { w: side, h: side } };
  const answer = {
    v: DESIGN_ANSWER_VERSION,
    layers: [{ id: 'pair-qr', kind: 'qr' as const, box: { x: side / 2, y: side / 2, w: side, h: side }, url }],
  };
  try {
    const out = await raw.call(ctx.renderer, zoneScene(config, answer, 220 / side), 1);
    if (!out || out.width <= 0) return;
    const canvas = el('canvas', 'pzd-qr-canvas');
    canvas.width = out.width;
    canvas.height = out.height;
    const bytes = new Uint8ClampedArray(out.pixels.byteLength);
    bytes.set(out.pixels);
    canvas.getContext('2d')?.putImageData(new ImageData(bytes, out.width, out.height), 0, 0);
    host.textContent = '';
    host.append(canvas);
  } catch {
    /* the tappable link below is the fallback */
  }
}

